import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import {
  createPairingToken,
  type PairingTokenDto,
} from '../api/pairing-api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  buildingId: string;
  propertyName?: string;
  buildingName?: string;
}

export function PhonePairingPanel({
  isOpen,
  onClose,
  propertyId,
  buildingId,
  propertyName,
  buildingName,
}: Props) {
  const [current, setCurrent] = useState<PairingTokenDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // The server's `url` is composed from the API's FrontendHost config, which
  // drifts between environments (QA had it pointing at localhost). The browser
  // already knows exactly where the PC session is running, so build the QR
  // URL from window.location.origin and the token we got back. Guarantees
  // the phone lands on the same hostname the PC is on.
  const pairingUrl = current
    ? `${window.location.origin}/setup/pair?token=${encodeURIComponent(current.token)}`
    : '';

  useEffect(() => {
    if (!isOpen) {
      setCurrent(null);
      setError(null);
      return;
    }
    setError(null);
    setCreating(true);
    createPairingToken({ propertyId, buildingId })
      .then((token) => setCurrent(token))
      .catch((err) => {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg ?? 'Failed to create pairing link. Try again.');
      })
      .finally(() => setCreating(false));
  }, [isOpen, propertyId, buildingId]);

  async function handleCopy() {
    if (!pairingUrl) return;
    try {
      await navigator.clipboard.writeText(pairingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked — fall back silently
    }
  }

  const scopeLabel = [propertyName, buildingName].filter(Boolean).join(' / ');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pair a phone" size="md">
      <div className="space-y-5">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Scan the QR with your phone to continue setting up meters on the phone's camera. The link stays valid for 8 hours.
        </p>
        {scopeLabel && (
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {scopeLabel}
          </p>
        )}

        {creating && (
          <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            Generating pairing link…
          </div>
        )}

        {error && (
          <div className="text-sm text-error-600 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {current && !creating && (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200 dark:border-gray-700">
              <QRCodeSVG value={pairingUrl} size={192} />
            </div>
            <p className="w-full text-xs font-mono text-center break-all text-gray-500 dark:text-gray-400">
              {pairingUrl}
            </p>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
